import { useEffect, useMemo } from 'react';
import { useLang } from '../../../../../../../../hooks/use-lang';
import { useQueryParams } from '../../../../../../../../hooks/use-query-params';
import { useToggle } from '../../../../../../../../hooks/use-toggle';
import { useAlertStore } from '../../../../../../../../store/use-alert-store';
import { useBranchesStore } from '../../../../../../../../store/use-branch-store';
import { useDishMenuStore } from '../../../../../../../../store/use-dish-menu-store';
import { useImageStore } from '../../../../../../../../store/use-image-store';
import { useInputLangStore } from '../../../../../../../../store/use-input-lang-store';
import { useQrStore } from '../../../../../../../../store/use-qr-store';
import { useTagStaffStore } from '../../../../../../../../store/use-tag-staff-store';
import { IdValue } from '../../../../../../../../types/props';
import { getMenuId } from '../../../menu-informer-helpers';

export const useMenuInformerCreateMenuModalStep1 = () => {
  const { menu, handleUpdateMenu, putMenu, postMenu } = useDishMenuStore();
  const { t, getLocale } = useLang();
  const { postImage, isLoading: isLoadingImage } = useImageStore();
  const { add: addError } = useAlertStore();
  const { language } = useInputLangStore();

  const { fetchTags, tags } = useTagStaffStore();
  const { fetchQrList, staticList } = useQrStore();
  const { place: places } = useBranchesStore();

  const [open, , setOpen] = useToggle();

  const handleChangePhoto = async (e?: File[]) => {
    if (!e) {
      handleUpdateMenu({ images: undefined });
      return;
    }
    const formData = new FormData();
    formData.append('image', e[0]);
    const resp = await postImage(formData);
    if (resp) {
      handleUpdateMenu({ images: { ...menu?.images, main: { ...resp } } });
    }
  };

  const { getQueryParams, addQueryParams } = useQueryParams();

  const params = getQueryParams();
  const branchId = params.branchId || menu?.place_id;

  useEffect(() => {
    fetchTags({ is_by_company: true, n: 1000, since_id: 0 });
    fetchQrList({ is_by_company: true, page: 1, page_size: 1000 });
  }, [fetchTags, fetchQrList]);

  const branchIdValues =
    places?.map((item) => ({ id: String(item.id), value: getLocale(item.name, language) })) || [];

  const handleSelectBranch = (e: IdValue) => {
    addQueryParams({ branchId: e.id });
    handleUpdateMenu({ place_id: Number(e.id) });
  };

  const { availableQrs, selectedQrs } = useMemo(() => {
    const availableQrs =
      staticList
        ?.filter((item) => String(item.place_id) === String(branchId))
        ?.map((item) => ({
          id: String(item.id),
          value: getLocale(item.name, language),
        })) || [];
    const selectedQrs = availableQrs.filter((item) => menu?.qr_ids?.includes(item.id));

    return { availableQrs, selectedQrs };
  }, [branchId, staticList, language, menu?.qr_ids]);

  const selectedTags = useMemo(
    () => tags?.filter((item) => menu?.tag_ids?.includes(Number(item.id))),
    [tags, menu?.tag_ids],
  );

  const handleSave = async () => {
    if (!menu?.place_id) handleUpdateMenu({ place_id: Number(branchId) });

    if (getMenuId()) {
      await putMenu();
    } else {
      const menu = await postMenu();
      addQueryParams({ menuId: menu.id });
    }
    setOpen(false);
  };

  return {
    t,
    menu,
    isLoadingImage,
    selectedTags,
    selectedQrs,
    tags,
    availableQrs,
    handleChangePhoto,
    handleUpdateMenu,
    addError,
    handleSelectBranch,
    open,
    branchIdValues,
    setOpen,
    branchId,
    handleSave,
  };
};
